import {
	Directive,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	NgZone,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';

import { ReachPanEvent, ReachPanEventPoint } from './types';

interface HammerInstance {
	on(event: string, handler: (event: { center: { x: number; y: number } }) => void): void;
	get(
		recognizer: string
	): { set(options: { enable?: boolean; threshold?: number }): void } | undefined;
	destroy(): void;
}

type HammerConstructor = new (element: HTMLElement) => HammerInstance;

@Directive({
	selector: '[reachPan]',
	standalone: true,
	host: {
		class: '__pan-handler',
	},
})
export class ReachPanDirective implements OnInit, OnDestroy {
	@Input()
	public reachPan = true;

	@Output()
	public reachPanMove = new EventEmitter<ReachPanEvent>();

	@Output()
	public reachPanCancel = new EventEmitter<void>();

	@Output()
	public reachPanFinish = new EventEmitter<ReachPanEvent>();

	private initialPoint: ReachPanEventPoint = { x: 0, y: 0 };

	private readonly ID_KEY = '__reachPanIdArr';

	private get randomStr(): string {
		return `${Math.random()}`.slice(2);
	}

	private uniqueId = `${performance.now()}_${this.randomStr}_${this.randomStr}`;

	private previousHandlers: string[] = [];

	private hammerInstance: HammerInstance | null = null;

	private readonly elementRef = inject(ElementRef<HTMLElement>);
	private readonly ngZone = inject(NgZone);

	ngOnInit(): void {
		const HammerJS = (globalThis as Record<string, unknown>)['Hammer'] as
			HammerConstructor | undefined;

		if (HammerJS) {
			this.ngZone.runOutsideAngular(() => {
				// Use Hammer's default preset (pan/swipe restricted to the horizontal
				// direction) so the computed `touch-action` stays `pan-y` and native
				// vertical scrolling keeps working. This mirrors the old Angular
				// `HammerGestureConfig` overrides used before Angular 22.
				this.hammerInstance = new HammerJS(this.elementRef.nativeElement as HTMLElement);

				this.hammerInstance.get('pan')?.set({ threshold: 16 });
				this.hammerInstance.get('pinch')?.set({ enable: false });
				this.hammerInstance.get('rotate')?.set({ enable: false });

				this.hammerInstance.on('panmove', (event) => {
					this.ngZone.run(() => this.panMove(event));
				});

				this.hammerInstance.on('panend', (event) => {
					this.ngZone.run(() => this.panEnd(event));
				});

				this.hammerInstance.on('pancancel', () => {
					this.ngZone.run(() => this.panCancel());
				});
			});
		}
	}

	ngOnDestroy(): void {
		this.hammerInstance?.destroy();
		this.hammerInstance = null;
	}

	@HostListener('mousedown', ['$event'])
	@HostListener('touchstart', ['$event'])
	public touchStart(event: TouchEvent | MouseEvent): void {
		this.onlyWhenListening(() => {
			const pointX = 'touches' in event ? (event.touches[0]?.pageX ?? 0) : event.pageX;
			const pointY = 'touches' in event ? (event.touches[0]?.pageY ?? 0) : event.pageY;

			this.handleStart(event);
			this.initialPoint = { x: pointX, y: pointY };
		});
	}

	public panMove(event: { center: { x: number; y: number } }): void {
		this.onlyWhenListening(() => {
			this.emit(this.reachPanMove, event);
		});
	}

	public panEnd(event: { center: { x: number; y: number } }): void {
		this.onlyWhenListening(() => {
			this.emit(this.reachPanFinish, event);
		});
	}

	public panCancel(): void {
		this.onlyWhenListening(() => {
			this.reachPanCancel.emit();
		});
	}

	private onlyWhenListening(cb: () => void): void {
		if (this.reachPan) {
			cb();
		}
	}

	private cleanPoint(point: ReachPanEventPoint): ReachPanEventPoint {
		return {
			x: point.x - this.initialPoint.x,
			y: point.y - this.initialPoint.y,
		};
	}

	private emit(
		emitter: EventEmitter<ReachPanEvent>,
		panEvent: { center: { x: number; y: number } }
	): void {
		const currentPoint: ReachPanEventPoint = {
			x: panEvent.center.x,
			y: panEvent.center.y,
		};
		const point = this.cleanPoint(currentPoint);

		emitter.emit({
			startingPoint: this.initialPoint,
			rawPoint: currentPoint,
			point,
			previousHandlers: this.previousHandlers.length > 0,
		});
	}

	private handleStart(event: TouchEvent | MouseEvent): void {
		const customEvent = event as (TouchEvent | MouseEvent) & Record<string, string[]>;
		this.previousHandlers = customEvent[this.ID_KEY] ?? [];
		customEvent[this.ID_KEY] = [...this.previousHandlers, this.uniqueId];
	}
}
